"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "../utils/className";
import { useActiveNavItem } from "../hooks/useActiveNavItem";
import { useScrollToSection } from "../hooks/useScrollToSection";

interface NavItem {
  id: string;
  label: string;
}

/** Section anchors on the home page, in document order. */
const NAV_ITEMS: NavItem[] = [
  { id: "capabilities", label: "Capabilities" },
  { id: "sectors", label: "Sectors" },
  { id: "terrakio", label: "Terrak.io" },
  { id: "our-work", label: "Our work" },
  { id: "about", label: "About" },
];

const HamburgerIcon = ({ isOpen }: { isOpen: boolean }) => (
  <span className="relative block h-4 w-6" aria-hidden="true">
    <span
      className={cn(
        "absolute left-0 block h-px w-6 bg-paper transition-transform duration-300",
        isOpen ? "top-2 rotate-45" : "top-0"
      )}
    />
    <span
      className={cn(
        "absolute left-0 top-2 block h-px w-6 bg-paper transition-opacity duration-200",
        isOpen && "opacity-0"
      )}
    />
    <span
      className={cn(
        "absolute left-0 block h-px w-6 bg-paper transition-transform duration-300",
        isOpen ? "top-2 -rotate-45" : "top-4"
      )}
    />
  </span>
);

export const NavBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const activeItem = useActiveNavItem();
  const pathname = usePathname();
  const [lastPathname, setLastPathname] = useState(pathname);
  const scrollToSection = useScrollToSection(() => setMenuOpen(false));

  // The drawer overlays the page; releasing the lock on close keeps the
  // ScrollSmoother wrapper from being measured while the body is frozen.
  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  // Close the drawer on navigation. Adjusting state during render is React's
  // recommended alternative to clearing it from an effect, which the lint rule
  // flags as a cascading render.
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setMenuOpen(false);
  }

  const isWorkRoute = pathname.startsWith("/case-studies");

  return (
    <header
      id="header-nav-bar"
      className="fixed inset-x-0 top-0 z-100 border-b border-white/[.07] bg-[linear-gradient(180deg,rgba(3,12,22,.92),rgba(3,12,22,.62))] backdrop-blur-[16px]"
    >
      <div className="dc-shell flex items-center justify-between gap-6 px-[clamp(16px,4vw,64px)] py-3.5 min-[1728px]:px-0">
        <Link href="/" className="flex items-center" aria-label="Haizea Analytics — home">
          <Image
            src="/images/logo.png"
            alt="Haizea Analytics"
            width={152}
            height={64}
            priority
            className="h-9 w-auto object-contain lg:h-11"
            draggable={false}
          />
        </Link>

        <nav className="hidden items-center gap-[clamp(16px,2vw,34px)] lg:flex">
          {NAV_ITEMS.map((item) => {
            const isActive =
              activeItem === item.id || (item.id === "our-work" && isWorkRoute);
            return (
              <Link
                key={item.id}
                href={`/#${item.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection(item.id);
                }}
                className={cn(
                  "border-b px-px py-1.5 text-[15px] font-medium transition-colors duration-300",
                  isActive
                    ? "border-mint text-mint"
                    : "border-transparent text-mist-2 hover:text-paper"
                )}
              >
                {item.label}
              </Link>
            );
          })}
          <Link
            href="/#contact"
            onClick={(event) => {
              event.preventDefault();
              scrollToSection("contact");
            }}
            className="bg-mint px-6 py-3 text-[15px] font-semibold text-ink transition-colors duration-300 hover:bg-paper"
          >
            Bring us a question
          </Link>
        </nav>

        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation menu"
          className="flex cursor-pointer items-center justify-center lg:hidden"
        >
          <HamburgerIcon isOpen={menuOpen} />
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          "overflow-hidden border-t border-white/[.07] bg-ink transition-[max-height,opacity] duration-[400ms] lg:hidden",
          menuOpen ? "max-h-[70vh] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <nav className="flex flex-col px-[clamp(16px,4vw,64px)] py-4">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.id}
              href={`/#${item.id}`}
              onClick={(event) => {
                event.preventDefault();
                scrollToSection(item.id);
              }}
              className="border-b border-white/[.08] py-4 text-lg font-medium text-mist-2 transition-colors hover:text-mint"
            >
              {item.label}
            </Link>
          ))}
          <Link
            href="/#contact"
            onClick={(event) => {
              event.preventDefault();
              scrollToSection("contact");
            }}
            className="mt-6 bg-mint px-5 py-4 text-center text-base font-semibold text-ink"
          >
            Bring us a question
          </Link>
        </nav>
      </div>
    </header>
  );
};
